import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { predict, type Prediction } from '../shared/predict'
import type { HouseholdSettings, Locale, Snapshot } from '../shared/types'
import * as api from './api'
import { getDict } from './i18n'

type Status = 'loading' | 'anonymous' | 'ready' | 'error'

interface StoreValue {
  status: Status
  error: string | null
  snapshot: Snapshot | null
  /** 由快照实时算出来的预测，数据一变自动重算 */
  prediction: Prediction | null
  signIn(name: string, passphrase: string): Promise<void>
  signUp(input: api.SignupInput): Promise<void>
  joinWithCode(code: string, input: api.JoinInput): Promise<void>
  resetPassword(token: string, loginName: string, passphrase: string): Promise<void>
  signOut(): void
  reload(): Promise<void>
  saveDay(date: string, payload: api.DayPayload): Promise<void>
  updateSettings(s: Partial<HouseholdSettings>): Promise<void>
  /** 把界面语言记到账号上（邮件按它来）。界面本身不等这一趟。 */
  syncLocale(locale: Locale): Promise<void>
}

const StoreContext = createContext<StoreValue | null>(null)

export function StoreProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState<string | null>(null)
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)

  const load = useCallback(async () => {
    if (!api.getToken()) {
      setStatus('anonymous')
      return
    }
    try {
      setSnapshot(await api.fetchSnapshot())
      setStatus('ready')
      setError(null)
    } catch (e) {
      if (e instanceof api.ApiError && e.status === 401) {
        setStatus('anonymous')
      } else {
        setError(e instanceof Error ? e.message : getDict().common.error)
        setStatus('error')
      }
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // 从后台切回前台时刷新一下 —— 跨过午夜后「今天」变了，圆环也得跟着变
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && api.getToken()) void load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load])

  const signIn = useCallback(async (name: string, passphrase: string) => {
    await api.login(name, passphrase)
    setSnapshot(await api.fetchSnapshot())
    setStatus('ready')
    setError(null)
  }, [])

  const signUp = useCallback(async (input: api.SignupInput) => {
    await api.signup(input)
    setSnapshot(await api.fetchSnapshot())
    setStatus('ready')
    setError(null)
  }, [])

  const joinWithCode = useCallback(async (code: string, input: api.JoinInput) => {
    await api.join(code, input)
    setSnapshot(await api.fetchSnapshot())
    setStatus('ready')
    setError(null)
  }, [])

  const resetPassword = useCallback(
    async (token: string, loginName: string, passphrase: string) => {
      await api.resetPassword(token, loginName, passphrase)
      setSnapshot(await api.fetchSnapshot())
      setStatus('ready')
      setError(null)
    },
    [],
  )

  const signOut = useCallback(() => {
    api.setToken(null)
    setSnapshot(null)
    setStatus('anonymous')
  }, [])

  const saveDay = useCallback(async (date: string, payload: api.DayPayload) => {
    // 服务端回的是完整快照，直接替换，省掉一套本地增量合并的逻辑
    setSnapshot(await api.saveDay(date, payload))
  }, [])

  const updateSettings = useCallback(async (s: Partial<HouseholdSettings>) => {
    const res = await api.saveSettings(s)
    setSnapshot((prev) =>
      prev ? { ...prev, settings: res.settings, today: res.today } : prev,
    )
  }, [])

  const syncLocale = useCallback(async (locale: Locale) => {
    await api.setLocale(locale)
    // 顺手更新本地快照，否则 App 里那个「服务端和设备不一致就同步一次」的
    // 判断会一直成立，每次快照变动都白发一次请求
    setSnapshot((prev) => (prev ? { ...prev, user: { ...prev.user, locale } } : prev))
  }, [])

  const prediction = useMemo(() => {
    if (!snapshot) return null
    return predict({
      periods: snapshot.periods,
      today: snapshot.today,
      defaultCycleLen: snapshot.settings.defaultCycleLen,
      defaultPeriodLen: snapshot.settings.defaultPeriodLen,
      // 开关关着的时候不参与计算，免得半截数据反而把排卵日带偏
      bbt: snapshot.settings.trackBbt ? snapshot.bbt : undefined,
      lh: snapshot.settings.trackLh ? snapshot.lh : undefined,
    })
  }, [snapshot])

  const value = useMemo<StoreValue>(
    () => ({
      status,
      error,
      snapshot,
      prediction,
      signIn,
      signUp,
      joinWithCode,
      resetPassword,
      signOut,
      reload: load,
      saveDay,
      updateSettings,
      syncLocale,
    }),
    [
      status,
      error,
      snapshot,
      prediction,
      signIn,
      signUp,
      joinWithCode,
      resetPassword,
      signOut,
      load,
      saveDay,
      updateSettings,
      syncLocale,
    ],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useStore(): StoreValue {
  const v = useContext(StoreContext)
  if (!v) throw new Error('useStore 必须在 StoreProvider 内使用')
  return v
}

/** 已登录页面里用，省掉到处判空 */
export function useReady() {
  const s = useStore()
  if (!s.snapshot || !s.prediction) throw new Error('数据尚未就绪')
  return { ...s, snapshot: s.snapshot, prediction: s.prediction }
}
